import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-test-run-grid>` says about itself.
 *
 * The scale is the design problem. The reference surface carries 103 checkboxes on first load, and a
 * flat list of 103 checkboxes is not a control anybody uses — they tick "all" and wait eleven
 * minutes. So the unit of selection is the CLASS, with its methods under it, tri-state: all, none,
 * or some. Picking three methods out of one class is two clicks rather than a hundred.
 *
 * "Select what failed" REPLACES the selection rather than adding to it, because re-running what
 * failed means what failed — quietly keeping fifty passing tests from the previous selection is the
 * slow answer to the question somebody asked in order to get a fast one.
 *
 * And Abort is a real control, not the run button greyed out: a run that cannot be stopped is a run
 * people avoid starting.
 */
const docs: ComponentDocs = {
  aliases: ['test picker', 'run tests', 'test selection', 'unit test runner', 'test class grid'],

  answers: [
    'choose which tests to run out of a long list',
    'run just the tests that failed last time',
    'run one class without ticking every method in it',
    'stop a run that is taking too long',
    'turn off coverage to make a run faster',
  ],

  placements: ['appPage'],

  whenToUse:
    'Wherever somebody chooses a subset of a large, two-level set to act on. It is written for tests, ' +
    'and the shape — classes with methods, tri-state parents, a count of what is picked — is the ' +
    'general one.',

  insteadUse: [
    {
      name: 'TestResultTable',
      when: 'the run has happened and the question is what it DID.',
    },
    {
      name: 'Tree',
      when: 'the hierarchy is deeper than two levels, or is being navigated rather than selected from.',
    },
  ],

  inputs: [
    {
      name: 'classes',
      type: 'json',
      description:
        'What can be run: `{ name, methods }[]`. A selection naming a test that is no longer in the ' +
        'list is dropped, so the count at the top never claims more than a run would contain.',
      default: [],
    },
    {
      name: 'selected',
      type: 'json',
      description: 'The tests picked in, as `Class.method` keys. Settable, so a surface can restore a selection.',
      default: [],
    },
    {
      name: 'failed',
      type: 'json',
      description:
        'Which tests failed in the last run, as `Class.method` keys. Marks them in the list and ' +
        'drives "Select what failed".',
      default: [],
    },
    {
      name: 'running',
      attribute: 'running',
      type: 'boolean',
      description:
        'A run is in flight. The primary control becomes Abort — a real control raising its own ' +
        'event, not the run button greyed out.',
      default: false,
    },
    {
      name: 'openWhenFinished',
      attribute: 'open-when-finished',
      type: 'boolean',
      description: 'Run option: show the run as soon as it finishes.',
      default: false,
    },
    {
      name: 'skipCoverage',
      attribute: 'skip-coverage',
      type: 'boolean',
      description: 'Run option: do not gather coverage. Faster, which is why somebody turns it off.',
      default: false,
    },
    {
      name: 'alwaysAsynchronous',
      attribute: 'always-asynchronous',
      type: 'boolean',
      description: 'Run option: always run asynchronously, even for a selection small enough to run inline.',
      default: false,
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say when there are no test classes.',
      default: 'No test classes found.',
    },
  ],

  events: [
    {
      name: 'caos-test-run',
      when: 'Run is pressed with something selected.',
      carries:
        '`{ tests, options }` — the three run options travel WITH the run rather than being remembered ' +
        'here, because they change what the application does with a run it has been asked for.',
    },
    { name: 'caos-test-abort', when: 'Abort is pressed while `running`.', carries: 'nothing' },
    { name: 'caos-test-selection', when: 'The selection changes, by any of the three ways it can be changed.', carries: '`{ selected }`.' },
    { name: 'caos-test-options', when: 'A run option is toggled.', carries: 'The three options as an object.' },
  ],

  state: [
    { name: 'selected', type: 'string[]', description: 'The picked tests, in the order the classes are listed.' },
    { name: 'options', type: 'CaosTestRunOptions', description: 'The three run options as they currently stand.' },
    { name: 'totalMethods', type: 'number', description: 'How many tests there are in total.' },
  ],

  parts: [
    { part: 'grid', is: 'The outer column.' },
    { part: 'summary', is: 'The "N of M tests selected" line.' },
    { part: 'options', is: 'The run-option row.' },
    { part: 'option', is: 'One run option.' },
    { part: 'classes', is: 'The scrolling list of classes.' },
    { part: 'class', is: 'One class and its methods.' },
    { part: 'class-toggle', is: 'A class’s own row and tri-state box.' },
    { part: 'methods', is: 'The methods under a class.' },
    { part: 'method', is: 'One method’s row.' },
    { part: 'actions', is: 'The run and re-run cluster.' },
    { part: 'run', is: 'The run button, which becomes Abort while running.' },
    { part: 'rerun-failed', is: 'The "select what failed" button.' },
  ],

  accessibility:
    'A partly-selected class sets `indeterminate` on its own box rather than showing empty, so the ' +
    'class row actually reports what is under it — that is what makes a hundred checkboxes into a ' +
    'control. Every box carries its own accessible name: a class names itself and its method count, ' +
    'a method names itself as `Class.method` and says "failed last run" when it did, so failure is ' +
    'spoken rather than conveyed by the red alone. The count line is a `role="status"` live region, ' +
    'so ticking a class announces what that actually selected. "Select what failed" is disabled, not ' +
    'hidden, with nothing failed — an absent control would read as "this run had no failures" on a ' +
    'screen that has not run anything.',

  examples: [
    {
      key: 'partial',
      label: 'Three methods out of two classes',
      summary:
        'The case a flat list of checkboxes cannot show: one class fully picked, one partly. The ' +
        'partly-picked class reports it on its own box rather than looking empty.',
      placement: {
        component: 'caos-test-run-grid',
        inputs: {
          selected: ['BillingServiceTest.chargesTax', 'BillingServiceTest.roundsHalfUp', 'AccountTriggerTest.insertsHandler'],
          classes: [
            { name: 'BillingServiceTest', methods: ['chargesTax', 'roundsHalfUp', 'splitsByLine', 'refundsWhole'] },
            { name: 'AccountTriggerTest', methods: ['insertsHandler'] },
            { name: 'QuoteCalculatorTest', methods: ['appliesDiscount', 'clampsToFloor'] },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'failures',
      label: 'After a run with failures',
      summary:
        'The failed tests are marked in the list and "Select what failed" is enabled. Pressing it ' +
        'REPLACES the selection — re-running what failed means what failed.',
      placement: {
        component: 'caos-test-run-grid',
        inputs: {
          failed: ['BillingServiceTest.roundsHalfUp', 'QuoteCalculatorTest.clampsToFloor'],
          classes: [
            { name: 'BillingServiceTest', methods: ['chargesTax', 'roundsHalfUp', 'splitsByLine'] },
            { name: 'QuoteCalculatorTest', methods: ['appliesDiscount', 'clampsToFloor'] },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'running',
      label: 'A run in flight',
      summary: 'Abort is a real control raising its own event, not the run button greyed out.',
      placement: {
        component: 'caos-test-run-grid',
        inputs: {
          running: true,
          'skip-coverage': true,
          selected: ['BillingServiceTest.chargesTax'],
          classes: [{ name: 'BillingServiceTest', methods: ['chargesTax', 'roundsHalfUp'] }],
        },
      },
      stage: { needsWidth: 560 },
    },
  ],
};

export default docs;
